/**
 * The animal data table (FD §3.6, Advanced): every value can be corrected. Corrections are shared: stored once
 * on the server for every plan and both people. Each corrected value shows who changed it and "Reset".
 */
import { useMemo, useState } from 'react';
import * as api from '../../../api';
import type { AnimalAreaKind, Biome, SpaceNeed, SpeciesOverrideField, SpeciesWithOverrides } from '../../../../shared/types';
import { BIOMES } from '../../../render/palette';
import { loadSpecies, type EditorStore } from '../../../store/editor';
import { Dialog, errorText, toast } from '../../shell/dialogs';
import { timeAgo } from '../../shell/format';

const KINDS: { k: AnimalAreaKind; label: string }[] = [{ k: 'habitat', label: 'Habitat' }, { k: 'aviary', label: 'Aviary' }, { k: 'aquarium', label: 'Aquarium' }];

/** The dialog with the table. */
export function SpeciesTable({ store, onClose }: { store: EditorStore; onClose: () => void }) {
  const [list, setList] = useState<SpeciesWithOverrides[]>(store.getState().species ?? []);
  const [filter, setFilter] = useState('');
  const [busy, setBusy] = useState<string | null>(null);

  const shown = useMemo(() => {
    const f = filter.trim().toLowerCase();
    return list.filter((s) => !f || s.name.toLowerCase().includes(f)).sort((a, b) => a.name.localeCompare(b.name));
  }, [list, filter]);

  const replace = async (next: SpeciesWithOverrides) => {
    const all = list.map((s) => (s.id === next.id ? next : s));
    setList(all);
    store.setSpecies(all);
    await loadSpecies(true).then((fresh) => store.setSpecies(fresh)).catch(() => undefined);
  };
  const save = async (s: SpeciesWithOverrides, field: SpeciesOverrideField, value: unknown) => {
    setBusy(`${s.id}:${field}`);
    try { await replace(await api.setOverride(s.id, field, value)); }
    catch (e) { toast(errorText(e), 'error'); }
    finally { setBusy(null); }
  };
  const reset = async (s: SpeciesWithOverrides, field: SpeciesOverrideField) => {
    setBusy(`${s.id}:${field}`);
    try { await replace(await api.clearOverride(s.id, field)); }
    catch (e) { toast(errorText(e), 'error'); }
    finally { setBusy(null); }
  };

  const who = (s: SpeciesWithOverrides, field: SpeciesOverrideField) => {
    const o = s.overridden[field];
    if (!o) return null;
    return (
      <span className="who">
        Changed by {o.by} {timeAgo(o.at)} ·{' '}
        <button type="button" className="link" disabled={busy !== null} onClick={() => void reset(s, field)} title={`Built-in: ${describe(o.builtIn)}`}>Reset to built-in</button>
      </span>
    );
  };
  const space = (s: SpeciesWithOverrides, field: 'area' | 'water') => {
    const v = s[field];
    return (
      <td className={s.overridden[field] ? 'changed' : ''}>
        <span className="row">
          <SpaceInput value={v.min} label={`${s.name} ${field} for the first animal`} onSave={(n) => void save(s, field, { ...v, min: n })} />
          <span className="muted">+</span>
          <SpaceInput value={v.perExtra} label={`${s.name} ${field} per extra animal`} onSave={(n) => void save(s, field, { ...v, perExtra: n })} />
        </span>
        {who(s, field)}
      </td>
    );
  };

  return (
    <Dialog title="Animal data" onClose={onClose} wide>
      <div className="species-dialog-body">
        <p className="hint">
          Space is what the game asks for: the first animal, plus each extra animal (m²). Changes here are shared with everyone and every plan,
          and the checks update straight away. Values marked “estimate” couldn't be checked in the game yet.
        </p>
        <input type="search" placeholder="Find an animal…" value={filter} onChange={(e) => setFilter(e.target.value)} aria-label="Find an animal" style={{ marginBottom: '0.5rem' }} />
        <div className="species-table-wrap">
          <table className="species-table">
            <thead>
              <tr><th>Animal</th><th>Kind</th><th>Land</th><th>Space m² (first + each extra)</th><th>Water m² (first + each extra)</th><th>Group</th></tr>
            </thead>
            <tbody>
              {shown.map((s) => (
                <tr key={s.id}>
                  <td>{s.name}{s.estimate && <span className="tag">estimate</span>}</td>
                  <td className={s.overridden.kind ? 'changed' : ''}>
                    <select value={s.kind} aria-label={`${s.name} kind`} onChange={(e) => void save(s, 'kind', e.target.value)}>
                      {KINDS.map((k) => <option key={k.k} value={k.k}>{k.label}</option>)}
                    </select>
                    {who(s, 'kind')}
                  </td>
                  <td className={s.overridden.biome ? 'changed' : ''}>
                    <select value={s.biome} aria-label={`${s.name} land`} onChange={(e) => void save(s, 'biome', e.target.value as Biome)}>
                      {(Object.keys(BIOMES) as Biome[]).map((b) => <option key={b} value={b}>{BIOMES[b].label}</option>)}
                    </select>
                    {who(s, 'biome')}
                  </td>
                  {space(s, 'area')}
                  {space(s, 'water')}
                  <td className={s.overridden.groupSize ? 'changed' : ''}>
                    <span className="row">
                      <SpaceInput value={s.groupSize.min} label={`${s.name} smallest group`} onSave={(n) => void save(s, 'groupSize', group(Math.max(1, Math.round(n)), Math.max(s.groupSize.max, Math.round(n)), s.groupSize.typical))} />
                      <span className="muted">–</span>
                      <SpaceInput value={s.groupSize.max} label={`${s.name} largest group`} onSave={(n) => void save(s, 'groupSize', group(Math.min(s.groupSize.min, Math.max(1, Math.round(n))), Math.max(1, Math.round(n)), s.groupSize.typical))} />
                    </span>
                    {who(s, 'groupSize')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="dialog-actions"><button type="button" className="primary" onClick={onClose}>Done</button></div>
    </Dialog>
  );
}

/** A number box that saves when you leave it (or press Enter) and the value changed. */
function SpaceInput({ value, label, onSave }: { value: number; label: string; onSave: (v: number) => void }) {
  const [text, setText] = useState<string | null>(null);
  const shown = text ?? String(value);
  const commit = () => {
    if (text === null) return;
    const v = Number(text);
    setText(null);
    if (text !== '' && Number.isFinite(v) && v >= 0 && v !== value) onSave(v);
  };
  return (
    <input type="number" min={0} aria-label={label} value={shown} onChange={(e) => setText(e.target.value)} onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setText(null); }} />
  );
}

/** A group size with the usual number kept inside min–max. */
function group(min: number, max: number, typical: number) {
  return { min, max, typical: Math.min(max, Math.max(min, typical)) };
}

function describe(v: unknown): string {
  if (v && typeof v === 'object' && 'min' in v) {
    const o = v as SpaceNeed | { min: number; max: number };
    return 'perExtra' in o ? `${o.min} + ${o.perExtra}` : `${o.min}–${(o as { max: number }).max}`;
  }
  return String(v);
}
